import { ReactNode } from 'react';

export type BadgeVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'destructive'
  | 'success'
  | 'warning'
  | 'info';

export type BadgeSize = 'xs' | 'sm' | 'md' | 'lg';

export interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  className?: string;
  icon?: ReactNode;
}

const variantStyles: Record<BadgeVariant, string> = {
  primary:
    'bg-primary-700 text-white dark:bg-primary-dark-50 dark:text-primary-dark-800',
  secondary:
    'bg-secondary-200 text-secondary-900 dark:bg-secondary-dark-400 dark:text-secondary-dark-900',
  outline:
    'bg-white border border-secondary-400 text-secondary-900 dark:bg-transparent dark:border-secondary-dark-600 dark:text-secondary-dark-900',
  destructive:
    'bg-red-600 text-white dark:bg-red-950 dark:text-red-200 dark:ring-1 dark:ring-red-800',
  success:
    'bg-green-600 text-white dark:bg-green-950 dark:text-green-200 dark:ring-1 dark:ring-green-800',
  warning:
    'bg-primary-800 text-white dark:bg-primary-dark-100 dark:text-primary-dark-800',
  info: 'bg-blue-600 text-white dark:bg-blue-950 dark:text-blue-200 dark:ring-1 dark:ring-blue-800'
};

const sizeStyles: Record<BadgeSize, string> = {
  xs: '~px-1.5/2 ~py-0.5/1 ~text-[0.625rem]/xs',
  sm: '~px-2/2.5 ~py-0.5/1 ~text-xs/sm',
  md: '~px-2.5/3 ~py-1/1.5 ~text-sm/base',
  lg: '~px-3/4 ~py-1.5/2 ~text-base/lg'
};

const Badge = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  icon
}: BadgeProps) => {
  const baseClasses =
    'inline-flex items-center justify-center rounded-md font-medium shadow-sm';
  const variantClass = variantStyles[variant];
  const sizeClass = sizeStyles[size];

  const badgeClassName =
    `${baseClasses} ${variantClass} ${sizeClass} ${className}`.trim();

  return (
    <span className={badgeClassName}>
      {icon && <span className='mr-1 flex items-center'>{icon}</span>}
      {children}
    </span>
  );
};

// Named exports for variants
export const BadgePrimary = (props: Omit<BadgeProps, 'variant'>) => (
  <Badge {...props} variant='primary' />
);

export const BadgeSecondary = (props: Omit<BadgeProps, 'variant'>) => (
  <Badge {...props} variant='secondary' />
);

export const BadgeOutline = (props: Omit<BadgeProps, 'variant'>) => (
  <Badge {...props} variant='outline' />
);

export const BadgeDestructive = (props: Omit<BadgeProps, 'variant'>) => (
  <Badge {...props} variant='destructive' />
);

export const BadgeSuccess = (props: Omit<BadgeProps, 'variant'>) => (
  <Badge {...props} variant='success' />
);

export const BadgeWarning = (props: Omit<BadgeProps, 'variant'>) => (
  <Badge {...props} variant='warning' />
);

export const BadgeInfo = (props: Omit<BadgeProps, 'variant'>) => (
  <Badge {...props} variant='info' />
);

// Named exports for sizes
export const BadgeXs = (props: Omit<BadgeProps, 'size'>) => (
  <Badge {...props} size='xs' />
);

export const BadgeSm = (props: Omit<BadgeProps, 'size'>) => (
  <Badge {...props} size='sm' />
);

export const BadgeMd = (props: Omit<BadgeProps, 'size'>) => (
  <Badge {...props} size='md' />
);

export const BadgeLg = (props: Omit<BadgeProps, 'size'>) => (
  <Badge {...props} size='lg' />
);

export default Badge;
